/**
 * MZIHO 询价/联系表单的附件上传（2026-09-12，2026-09-17 换掉原生文件控件）
 *
 * 问题：商品页询价和「联系我们」都走 Shopify 原生 contact 端点，它带不了附件 ——
 *       邮件里只剩一个文件名，图本身从没离开客户的浏览器。
 * 做法：借购物车的文件上传通道。往 /cart/add.js 发 multipart，属性 properties[_Upload] 带文件，
 *       Shopify 把文件存进自己的 CDN、行项目属性里回一个 URL；拿到 URL 立刻删掉这一行，
 *       再把 URL 写进询价/联系邮件。店主在邮件里点链接就能看原图。
 *
 * 载体商品用 product-options（配置器收加价用的 $0.01 商品）。万一删除失败，
 * 购物车页的 sweepOrphanSurcharges() 会把「没有 parent 的 product-options 行」当孤儿清掉。
 *
 * API：   window.MzihoUpload.toLink(file, carrierVariantId) -> Promise<url>
 * 联系页：[data-mziho-contact-upload] 容器自动接线（blocks/contact-form.liquid 渲染）。
 * 2026-11-03：联系页表单改成不刷新发送 + 原地「Message sent」卡片，见下面 sendInPlace()。
 */
(() => {
  if (window.MzihoUpload) return;

  const MAX_BYTES = 20 * 1024 * 1024;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const toLink = async (file, carrierId) => {
    if (!file) return '';
    if (!carrierId) throw new Error('upload carrier missing');
    if (file.size > MAX_BYTES) throw new Error('file too large');

    const body = new FormData();
    body.append('id', String(carrierId));
    body.append('quantity', '1');
    body.append('properties[_Upload]', file, file.name);
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      body,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data) throw new Error(`upload failed (${res.status})`);

    const item = Array.isArray(data.items) ? data.items[0] : data;
    const raw = (item && item.properties && item.properties._Upload) || '';

    // 临时行立刻删掉。删不掉也没关系：购物车页的孤儿清理会兜底。
    if (item && item.key) {
      try {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      } catch (_error) { /* 见上 */ }
    }

    if (!raw || !/^(https?:)?\/\//.test(raw)) throw new Error('no file url returned');
    return raw.startsWith('//') ? `https:${raw}` : raw;
  };

  window.MzihoUpload = { toLink, MAX_BYTES };

  /* ---------------- 「联系我们」表单接线 ----------------
     文件框**不带 name**：原生 contact 表单是 urlencoded，带 name 的文件框会被当成
     一个空字段提交。链接写进一个隐藏字段，有了值才给它 name，没附件时邮件里不出现空行。

     提交顺序：我们的 submit 监听挂在 form 的捕获阶段 —— 有文件且还没传时先拦下，
     传完再重新提交。Shopify 的验证码监听在 form 级监听之后才跑（2026-09-01 实测），
     所以这里拦得住；重新提交时放行，验证码照常求解。
     重新提交的方式和商品页询价一致：验证码绑上了用 requestSubmit()，没绑上用 submit()。 */
  const captchaBound = (form) => form.dataset.hcaptchaBound === 'true' || form.dataset.recaptchaBound === 'true';

  const NO_FILE = 'No file chosen';

  const wire = (box) => {
    if (box.dataset.mzihoWired) return;
    box.dataset.mzihoWired = '1';
    const form = box.closest('form');
    const fileInput = box.querySelector('[data-mziho-contact-file]');
    const link = box.querySelector('[data-mziho-contact-link]');
    const status = box.querySelector('[data-mziho-contact-status]');
    const nameOut = box.querySelector('[data-mziho-contact-name]');
    if (!form || !fileInput || !link) return;
    const hint = status ? status.textContent : '';
    // 手写在页面正文里的上传框不带载体 ID，回退到 page.liquid 给的那个。
    const carrier = box.dataset.carrier || window.MzihoUploadCarrier || '';

    fileInput.addEventListener('change', () => {
      box.dataset.done = '';
      link.value = '';
      link.removeAttribute('name');
      const file = fileInput.files && fileInput.files[0];
      if (nameOut) nameOut.textContent = file ? file.name : NO_FILE;
      if (!status) return;
      if (!file) status.textContent = hint;
      else if (file.size > MAX_BYTES) status.textContent = 'This file is over 20 MB. Please email it to us instead.';
      else status.textContent = hint;
    });

    form.addEventListener('submit', async (event) => {
      const file = fileInput.files && fileInput.files[0];
      if (!file || box.dataset.done === '1') return;   // 没附件 / 已传完：交给原流程（含验证码）
      event.preventDefault();
      event.stopImmediatePropagation();

      const button = form.querySelector('[type="submit"]');
      if (button) button.disabled = true;
      if (status) status.textContent = 'Uploading your file…';
      try {
        link.value = await toLink(file, carrier);
        if (status) status.textContent = 'File uploaded. Sending your message…';
      } catch (error) {
        console.warn('[mziho] contact upload failed', error);
        link.value = `${file.name} (upload failed, please ask the customer to email this file)`;
        if (status) status.textContent = 'We could not upload your file. Your message will still be sent; please email the file to us.';
      }
      link.name = link.dataset.name || 'contact[Attachment]';
      box.dataset.done = '1';
      if (button) button.disabled = false;
      if (captchaBound(form)) form.requestSubmit(button || undefined);
      else form.submit();
    }, true);
  };

  /* 页面正文里手写的联系表单没有上传框 —— Contact Us 页就是这种：form#contact_form 写在后台
     页面内容里，不是主题渲染的。sections/page.liquid 发现正文里有联系表单时，会给出
     window.MzihoUploadCarrier 并加载本脚本，这里在提交按钮上方补一个上传框，样式照抄该表单的内联样式。
     条件：提交到 /contact 且带留言框 textarea[name="contact[body]"] —— 页脚订阅表单、隐藏的询价表单
     都没有留言框；商品页不给载体 ID，「Ask an expert」弹窗也不会被插。

     2026-09-17：原生 <input type="file"> 的按钮文字和「未选择任何文件」在 shadow DOM 里，
     跟浏览器自己的界面语言走（Chrome 看的是「首选语言」列表，不一定等于手机系统语言），
     CSS 改不了里面的文字 —— 所以英文手机上照样会冒出中文，杵在一整页英文中间。
     做法：把原生控件视觉隐藏（保留可聚焦，不用 display:none，否则键盘用户够不到），
     用一个 <label for> 盖上去当按钮，文件名由 change 事件写进 [data-mziho-contact-name]。 */
  const LABEL_STYLE = 'display:block;margin-bottom:7px;color:#0B1F3A;font-size:14px;font-weight:700';
  const BOX_STYLE = 'position:relative;display:flex;align-items:center;gap:12px;width:100%;padding:10px 12px;border:1px dashed #C8D2DC;border-radius:7px;background:#FFFFFF;cursor:pointer';
  const BTN_STYLE = 'flex:none;padding:8px 16px;border:1px solid #C8D2DC;border-radius:5px;background:#F1F5F9;color:#0B1F3A;font-size:14px;font-weight:700';
  const NAME_STYLE = 'min-width:0;overflow:hidden;color:#7A8CA0;font-size:14px;text-overflow:ellipsis;white-space:nowrap';
  const FILE_STYLE = 'position:absolute;width:1px;height:1px;padding:0;border:0;opacity:0;overflow:hidden;clip:rect(0 0 0 0)';

  const inject = (form) => {
    if (form.querySelector('[data-mziho-contact-upload]')) return;
    if (!form.querySelector('textarea[name="contact[body]"]')) return;
    const carrier = window.MzihoUploadCarrier;
    if (!carrier) return;
    const id = `${form.id || 'MzihoContact'}-upload`;
    const box = document.createElement('div');
    box.className = 'mziho-contact-upload';
    box.dataset.mzihoContactUpload = '';
    box.dataset.carrier = String(carrier);
    box.style.cssText = 'position:relative;margin-bottom:20px';
    box.innerHTML = `<label for="${id}" style="${LABEL_STYLE}">Artwork or reference image <span style="color:#52657A;font-size:12px;font-weight:500">Optional</span></label>`
      + `<label for="${id}" style="${BOX_STYLE}">`
      + `<span style="${BTN_STYLE}">Choose File</span>`
      + `<span data-mziho-contact-name style="${NAME_STYLE}">${NO_FILE}</span>`
      + '</label>'
      + `<input type="file" id="${id}" accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.ai,.eps,.svg" data-mziho-contact-file style="${FILE_STYLE}">`
      + '<input type="hidden" value="" data-name="contact[Attachment]" data-mziho-contact-link>'
      + '<small data-mziho-contact-status style="display:block;margin-top:6px;color:#52657A;font-size:12px;line-height:1.5">JPG, PNG, PDF, AI, EPS or SVG, up to 20 MB.</small>';
    const button = form.querySelector('[type="submit"]');
    if (button) button.parentNode.insertBefore(box, button);
    else form.appendChild(box);
  };

  /* ---------------- 「联系我们」不刷新发送（2026-11-03） ----------------
     原来 Contact Us 是原生整页提交：点 Send 整页刷新，回来还是一张空表单，没有任何「已发送」提示
     （页面正文是后台 HTML，没法用 Liquid 判断 form.posted_successfully）。
     做法照抄购物车询价 mziho-email-quote.js：赶在验证码绑定（客户第一次 focus 表单）之前
     把 form.submit 换成 sendInPlace() —— 验证码拿到令牌后调的就是它，令牌照常带上。
       fetch POST /contact，跟随跳转后 URL 含 contact_posted=true → 表单原地换成成功卡片；
       含 /challenge（要人工验证）→ 打开 Shopify 验证页，验证后它补发并跳回（下面的 posted 兜底补卡片）；
       其它 → 按钮上方报错，按钮恢复可重试。🔴 不能拿用过的令牌原生重发：400 Invalid CAPTCHA token。
     和附件上传的先后：上传监听在捕获阶段，有文件时先拦下、传完再 requestSubmit()/submit()，
     两条路最后都落到 sendInPlace()。
     只接页面正文里的联系表单（有载体 ID + 留言框）；商品页/购物车询价表单归 mziho-email-quote.js。
     JS 失败时表单照常原生提交 —— 不挡客户。 */
  const contactPosted = /[?&]contact_posted=true/.test(location.search);

  const sendable = (form) => Boolean(window.MzihoUploadCarrier)
    && Boolean(form.querySelector('textarea[name="contact[body]"]'))
    && !form.matches('.mziho-email-quote__form');

  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  // 11-03 店主：配色照商品页 Custom 询价的成功卡片（.mziho-dynamic-quote__done：#dcfce7 底、#14532d 字、9px 圆角、
  // 「再发一封」是下划线文字链接），对勾 = 实心绿圆 + 白勾（店主同日从描边圆圈改选）
  const SENT_STYLE = 'padding:22px 24px;border-radius:9px;background:#DCFCE7;color:#14532D;line-height:1.5';
  const TICK_STYLE = 'display:inline-flex;flex:none;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;background:#16A34A;color:#FFFFFF';
  const TICK_SVG = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const AGAIN_STYLE = 'margin-top:14px;padding:0;border:0;background:none;color:inherit;cursor:pointer;font:inherit;font-size:15px;font-weight:700;text-decoration:underline;text-underline-offset:3px';

  // 成功卡片放在表单前面、表单藏起来；「Send another message」清空表单（含附件状态）再放回来
  const showSent = (form, email) => {
    const box = document.createElement('div');
    box.className = 'mziho-contact-sent';
    box.setAttribute('role', 'status');
    box.style.cssText = SENT_STYLE;
    box.innerHTML = `<strong style="display:flex;align-items:center;gap:10px;margin-bottom:8px;font-size:20px;line-height:1.3"><span style="${TICK_STYLE}">${TICK_SVG}</span>Message sent</strong>`
      // 长邮箱是一整串没有空格的字，手机上会顶出卡片被裁掉 —— overflow-wrap:anywhere 让它在卡片内折行
      + `<span style="display:block;font-size:15px;overflow-wrap:anywhere">Thank you. We'll reply${email ? ` to <b>${esc(email)}</b>` : ''} within one business day.</span>`
      + `<button type="button" style="${AGAIN_STYLE}">Send another message</button>`;
    box.querySelector('button').addEventListener('click', () => {
      box.remove();
      form.reset();
      // 附件框靠 change 事件清掉上次的链接和 done 标记，否则下一封会带上旧链接
      form.querySelector('[data-mziho-contact-file]')?.dispatchEvent(new Event('change'));
      form.hidden = false;
      form.querySelector('input[name="contact[name]"]')?.focus();
    });
    form.hidden = true;
    form.before(box);
    box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const showError = (form, message) => {
    let box = form.querySelector('.mziho-contact-error');
    if (!box) {
      if (!message) return;
      box = document.createElement('p');
      box.className = 'mziho-contact-error';
      box.setAttribute('role', 'alert');
      box.style.cssText = 'margin:0 0 14px;color:#B42318;font-size:14px;line-height:1.5';
      const button = form.querySelector('[type="submit"]');
      if (button) button.parentNode.insertBefore(box, button);
      else form.appendChild(box);
    }
    box.textContent = message;
    box.hidden = !message;
  };

  const setBusy = (form, busy) => {
    const button = form.querySelector('[type="submit"]');
    if (!button) return;
    if (!button.dataset.label) button.dataset.label = button.textContent;
    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
    button.textContent = busy ? 'Sending…' : button.dataset.label;
  };

  const sendInPlace = async (form) => {
    setBusy(form, true);
    showError(form, '');
    let response = null;
    try {
      response = await fetch(new URL(form.action, location.href).pathname, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
    } catch (_error) { /* 网络错误，走下面的报错 */ }
    const url = (response && response.url) || '';
    if (response && response.ok && url.includes('contact_posted=true')) {
      setBusy(form, false);
      showSent(form, form.querySelector('input[name="contact[email]"]')?.value.trim() || '');
      return;
    }
    if (url.includes('/challenge')) {
      location.assign(url);
      return;
    }
    setBusy(form, false);
    showError(form, 'We could not send your message just now. Please try again, or email us at support@mziho.com.');
  };

  const wireSend = (form) => {
    if (form.dataset.mzihoSendWired || !sendable(form)) return;
    form.dataset.mzihoSendWired = '1';
    // 必须赶在验证码绑定之前：绑定时它会把这时的 form.submit 存起来，拿到令牌后调用
    form.submit = () => sendInPlace(form);
    form.addEventListener('submit', (event) => {
      // 验证码已绑定：它自己的 submit 监听会 preventDefault → 求解 → 调上面的 sendInPlace()，这里不插手
      if (captchaBound(form)) return;
      event.preventDefault();
      sendInPlace(form);
    });
    // 整页提交回来（验证页跳回、或脚本当时没接上）：补一张成功卡片，再把 ?contact_posted 从地址栏去掉
    if (contactPosted) {
      showSent(form, '');
      const clean = new URL(location.href);
      clean.searchParams.delete('contact_posted');
      clean.hash = '';
      history.replaceState(history.state, '', clean);
    }
  };

  const run = () => {
    document.querySelectorAll('form[action*="/contact"]').forEach(inject);
    document.querySelectorAll('[data-mziho-contact-upload]').forEach(wire);
    document.querySelectorAll('form[action*="/contact"]').forEach(wireSend);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
})();
